/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/*
 * The file is automatically generated by Serenity UI.
 *
 * ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
 *
 * Found an issue? Submit a task to the ClickUp board:
 * https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
 */
import * as stylex from '@stylexjs/stylex';
import type { UseTooltipPortalOptions } from '@visx/tooltip/lib/hooks/useTooltipInPortal';
import {
  createContext,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { createPortal } from 'react-dom';
import { $ } from '../../tokens.stylex';
import { ListBoxItemContext } from '../list-box-item/list-box-item.component';
import { ListBoxContext } from '../list-box/list-box.component';
import { TextContext } from '../text/text.component';

interface ChartLegendProps {
  children?: ReactNode;
  offsetX?: number;
  offsetY?: number;
  style?: stylex.StyleXStyles;
  tooltipData?: { x: number; y: number } | { x: number; y: number[] };
  tooltipLeft?: number;
  tooltipTop?: number;
  debounce?: UseTooltipPortalOptions['debounce'];
  detectBounds?: UseTooltipPortalOptions['detectBounds'];
  scroll?: UseTooltipPortalOptions['scroll'];
  zIndex?: UseTooltipPortalOptions['zIndex'];
  portalContainer?: HTMLElement;
}

const ChartLegendContext = createContext<ContextValue<ChartLegendProps, HTMLDivElement>>({});

const ChartLegend = forwardRef((props: ChartLegendProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ChartLegendContext);

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    children,
    offsetX = 12,
    offsetY = -12,
    style,
    portalContainer,
    ...coreProps
  } = props;

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [clampedPosition, setClampedPosition] = useState<{ left: number; top: number } | null>(null);
  const [clampedMaxWidth, setClampedMaxWidth] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);

  const hasTooltip = !!tooltipData && tooltipLeft != null && tooltipTop != null;
  const tooltipContent = hasTooltip ? children : null;
  const showTooltip = hasTooltip && tooltipContent != null;

  const [enableTransition, setEnableTransition] = useState(false);
  const transitionFrames = useRef<{ first: number; second: number }>({ first: 0, second: 0 });

  const scheduleEnableTransition = useCallback(() => {
    if (transitionFrames.current.first) {
      cancelAnimationFrame(transitionFrames.current.first);
    }
    if (transitionFrames.current.second) {
      cancelAnimationFrame(transitionFrames.current.second);
    }
    setEnableTransition(false);
    transitionFrames.current.first = requestAnimationFrame(() => {
      transitionFrames.current.second = requestAnimationFrame(() => {
        setEnableTransition(true);
      });
    });
  }, []);

  useEffect(() => {
    if (!showTooltip) {
      setEnableTransition(false);
      return;
    }
    scheduleEnableTransition();
  }, [showTooltip, scheduleEnableTransition]);

  useEffect(
    () => () => {
      if (transitionFrames.current.first) {
        cancelAnimationFrame(transitionFrames.current.first);
      }
      if (transitionFrames.current.second) {
        cancelAnimationFrame(transitionFrames.current.second);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    if (!showTooltip || tooltipLeft == null || tooltipTop == null || !tooltipRef.current) {
      setClampedPosition(null);
      setClampedMaxWidth(null);
      setIsReady(false);
      return;
    }

    const tooltipEl = tooltipRef.current;
    const container = (tooltipEl.offsetParent as HTMLElement | null) ?? tooltipEl.parentElement;

    if (!container) {
      setClampedPosition(null);
      setClampedMaxWidth(null);
      setIsReady(false);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const desiredTop = tooltipTop + offsetY;
    const padding = 8;

    const scrollContainer = container.parentElement;
    const viewportRect = scrollContainer
      ? scrollContainer.getBoundingClientRect()
      : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };

    const viewportWidth = Math.max(0, viewportRect.right - viewportRect.left - padding * 2);
    const nextMaxWidth = viewportWidth || null;

    if (clampedMaxWidth !== nextMaxWidth) {
      setClampedMaxWidth(nextMaxWidth);
      setIsReady(false);
      return;
    }

    const tooltipRect = tooltipEl.getBoundingClientRect();
    const minLeft = viewportRect.left - containerRect.left + padding;
    const maxLeft = viewportRect.right - containerRect.left - tooltipRect.width - padding;
    const minTop = viewportRect.top - containerRect.top + padding;
    const maxTop = viewportRect.bottom - containerRect.top - tooltipRect.height - padding;

    const desiredRight = tooltipLeft + offsetX;
    const desiredLeft = tooltipLeft - tooltipRect.width - offsetX;
    const rightFits = desiredRight <= maxLeft;
    const leftFits = desiredLeft >= minLeft;
    const nextLeft =
      maxLeft < minLeft
        ? minLeft
        : Math.min(Math.max(rightFits || !leftFits ? desiredRight : desiredLeft, minLeft), maxLeft);
    const nextTop = maxTop < minTop ? minTop : Math.min(Math.max(desiredTop, minTop), maxTop);

    setClampedPosition((prev) =>
      prev && prev.left === nextLeft && prev.top === nextTop ? prev : { left: nextLeft, top: nextTop },
    );
    setIsReady(true);
  }, [showTooltip, tooltipLeft, tooltipTop, offsetX, offsetY, clampedMaxWidth]);

  if (!showTooltip) {
    return null;
  }

  const desiredLeft = tooltipLeft + offsetX;
  const desiredTop = tooltipTop + offsetY;
  const position = clampedPosition ?? { left: desiredLeft, top: desiredTop };

  const tooltipNode = (
    <div
      ref={tooltipRef}
      {...coreProps}
      {...stylex.props([
        tooltipStyles.base,
        isReady && enableTransition && tooltipStyles.transition,
        tooltipStyles.position(position),
        !isReady && tooltipStyles.hidden,
        style,
      ])}
    >
      <Provider
        values={[
          [TextContext, { size: 14 }],
          [
            ListBoxContext,
            {
              selectionMode: 'none',
              style: listBoxStyles.base,
            },
          ],
          [
            ListBoxItemContext,
            {
              style: listBoxItemStyles.base,
            },
          ],
        ]}
      >
        {tooltipContent}
      </Provider>
    </div>
  );

  return portalContainer ? createPortal(tooltipNode, portalContainer) : tooltipNode;
});

const tooltipStyles = stylex.create({
  base: {
    padding: 24,
    borderColor: $.border08,
    borderRadius: 12,
    borderStyle: 'solid',
    borderWidth: 1,
    gap: 16,
    alignItems: 'flex-start',
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: $.shadowBottom060Silent,
    display: 'flex',
    flexDirection: 'column',
    pointerEvents: 'none',
    position: 'absolute',
    willChange: 'left, top',
    zIndex: 3,
    maxWidth: '100%',
    width: 'max-content',
  },
  transition: {
    transitionDuration: '180ms',
    transitionProperty: 'left, top',
    transitionTimingFunction: 'ease-out',
  },
  position: (coords: { left: number; top: number }) => ({
    left: coords.left,
    top: coords.top,
  }),
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
});

const listBoxStyles = stylex.create({
  base: {
    gap: 8,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

const listBoxItemStyles = stylex.create({
  base: {
    gap: 8,
    paddingInline: 0,
    height: null,
    minWidth: 132,
  },
});

export { ChartLegend, ChartLegendContext, type ChartLegendProps };
